import { BranchCode, RealEstateEligibilityDtoStatusEnum } from "api/risk"
import { useEffect, useState } from "react"
import { risksAPI } from "services/swagger-api/riskApi"

export interface EligibilityVerdict {
  status: RealEstateEligibilityDtoStatusEnum
  reasons: string[]
  isEligible: boolean
}

interface UseEligibilityCheckOptions {
  /** GLI : réponse à la clause résolutoire (transmise au back). */
  terminationClauseGlobal?: boolean
  /** Incrémenter pour relancer l'évaluation (ex. après édition du bien). */
  refreshKey?: number
  /** Désactive l'appel quand false. */
  enabled?: boolean
}

/**
 * Évalue l'éligibilité d'un bien via le back (source unique GLI / CBNO),
 * en remplacement des règles autrefois hardcodées côté front.
 */
export const useEligibilityCheck = (
  branchCode: BranchCode,
  realEstateId: string | undefined,
  { terminationClauseGlobal, refreshKey, enabled = true }: UseEligibilityCheckOptions = {}
) => {
  const [verdict, setVerdict] = useState<EligibilityVerdict>()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!enabled || !realEstateId) {
      setVerdict(undefined)
      return
    }

    let cancelled = false

    const evaluate = async () => {
      setIsLoading(true)
      try {
        const response = await risksAPI.evaluateEligibility({
          evaluateEligibilityCommandDto: {
            branchCode,
            realEstateIds: [realEstateId],
            terminationClauseGlobal
          }
        })

        if (cancelled) return

        const result = response.realEstates?.[0]
        setVerdict(
          result
            ? {
                status: result.status as RealEstateEligibilityDtoStatusEnum,
                reasons: result.reasons ?? [],
                isEligible: result.status === RealEstateEligibilityDtoStatusEnum.Eligible
              }
            : undefined
        )
      } catch (error) {
        console.error("Erreur lors de l'évaluation d'éligibilité:", error)
        if (!cancelled) setVerdict(undefined)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    evaluate()

    return () => {
      cancelled = true
    }
  }, [branchCode, realEstateId, terminationClauseGlobal, refreshKey, enabled])

  return { verdict, isLoading }
}
